/**
 * Ground-plane math. The simulation lives on the XZ plane (1 unit = 1 metre, +Y up).
 *
 * Heading convention (matches three.js `object.rotation.y`):
 *   heading 0      -> facing +Z
 *   forward(h)     = (sin h, cos h)
 *   right(h)       = (-cos h, sin h)        (forward x up)
 *   positive angular velocity = counter-clockwise seen from above (+Y), i.e. turning LEFT.
 *
 * Plain `{x, z}` objects are used everywhere so data stays serialisable and allocation-light.
 * Functions suffixed with `Out`/`Into` write into a target to avoid allocations in hot loops.
 */

export interface V2 {
  x: number;
  z: number;
}

export const TAU = Math.PI * 2;
export const DEG = Math.PI / 180;

export function v2(x = 0, z = 0): V2 {
  return { x, z };
}
export function copy(a: V2): V2 {
  return { x: a.x, z: a.z };
}
export function set(out: V2, x: number, z: number): V2 {
  out.x = x;
  out.z = z;
  return out;
}
export function assign(out: V2, a: V2): V2 {
  out.x = a.x;
  out.z = a.z;
  return out;
}
export function add(a: V2, b: V2): V2 {
  return { x: a.x + b.x, z: a.z + b.z };
}
export function sub(a: V2, b: V2): V2 {
  return { x: a.x - b.x, z: a.z - b.z };
}
export function scale(a: V2, s: number): V2 {
  return { x: a.x * s, z: a.z * s };
}
export function addScaled(out: V2, a: V2, s: number): V2 {
  out.x += a.x * s;
  out.z += a.z * s;
  return out;
}
export function dot(a: V2, b: V2): number {
  return a.x * b.x + a.z * b.z;
}
/** Y component of the 3D cross product (a.x,0,a.z) x (b.x,0,b.z). */
export function crossY(a: V2, b: V2): number {
  return a.z * b.x - a.x * b.z;
}
/** (0,w,0) x (r.x,0,r.z): velocity of a point at offset r on a body spinning at w. */
export function crossWV(w: number, r: V2): V2 {
  return { x: w * r.z, z: -w * r.x };
}
export function lengthSq(a: V2): number {
  return a.x * a.x + a.z * a.z;
}
export function length(a: V2): number {
  return Math.sqrt(a.x * a.x + a.z * a.z);
}
export function distance(a: V2, b: V2): number {
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dz * dz);
}
export function distanceSq(a: V2, b: V2): number {
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  return dx * dx + dz * dz;
}
export function normalize(a: V2): V2 {
  const l = Math.sqrt(a.x * a.x + a.z * a.z);
  return l > 1e-9 ? { x: a.x / l, z: a.z / l } : { x: 0, z: 0 };
}
export function lerpV(a: V2, b: V2, t: number): V2 {
  return { x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t };
}
/** Rotate a vector by angle (same rotation three.js applies with rotation.y = angle). */
export function rotate(a: V2, angle: number): V2 {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  return { x: a.x * c + a.z * s, z: -a.x * s + a.z * c };
}
/** Inverse of `rotate`: world direction -> local direction. */
export function unrotate(a: V2, angle: number): V2 {
  return rotate(a, -angle);
}
export function forward(heading: number): V2 {
  return { x: Math.sin(heading), z: Math.cos(heading) };
}
export function right(heading: number): V2 {
  return { x: -Math.cos(heading), z: Math.sin(heading) };
}
/** Heading that faces along direction d. */
export function headingOf(d: V2): number {
  return Math.atan2(d.x, d.z);
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
export function inverseLerp(a: number, b: number, v: number): number {
  return a === b ? 0 : (v - a) / (b - a);
}
export function smoothstep(a: number, b: number, v: number): number {
  const t = clamp01(inverseLerp(a, b, v));
  return t * t * (3 - 2 * t);
}
/** Wrap an angle to (-PI, PI]. */
export function wrapAngle(a: number): number {
  a = a % TAU;
  if (a <= -Math.PI) a += TAU;
  else if (a > Math.PI) a -= TAU;
  return a;
}
/** Shortest signed difference b - a between two angles. */
export function angleDelta(a: number, b: number): number {
  return wrapAngle(b - a);
}
export function lerpAngle(a: number, b: number, t: number): number {
  return a + angleDelta(a, b) * t;
}
/** Move `v` toward `target` by at most `maxDelta`. */
export function approach(v: number, target: number, maxDelta: number): number {
  if (v < target) return Math.min(v + maxDelta, target);
  return Math.max(v - maxDelta, target);
}
/**
 * Frame-rate independent exponential smoothing factor.
 * `rate` is roughly "fraction recovered per second" expressed as 1/time-constant.
 * Usage: x += (target - x) * damp(rate, dt)
 */
export function damp(rate: number, dt: number): number {
  return 1 - Math.exp(-rate * dt);
}
export function sign(v: number): number {
  return v > 0 ? 1 : v < 0 ? -1 : 0;
}

/** Axis-aligned rectangle on the ground plane. */
export interface Rect {
  minX: number;
  minZ: number;
  maxX: number;
  maxZ: number;
}
export function rectContains(r: Rect, p: V2): boolean {
  return p.x >= r.minX && p.x <= r.maxX && p.z >= r.minZ && p.z <= r.maxZ;
}
export function rectCenter(r: Rect): V2 {
  return { x: (r.minX + r.maxX) / 2, z: (r.minZ + r.maxZ) / 2 };
}
